import React, { useMemo, useCallback } from "react";
import { Box, Slider, Typography } from "@mui/material";

export const DEFAULT_BUSINESS_HOURS_RANGE: [number, number] = [6, 18];

/** 12-hour clock with minutes for display (e.g. 6:00 AM, 12:00 PM). */
function formatHourClock(hour: number): string {
  const h = Math.max(0, Math.min(23, hour));
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:00 ${period}`;
}

const SLIDER_MARK_HOURS = [0, 6, 12, 18, 23] as const;

/** Ticks only — labels render in a separate row below so they never overlap the track. */
const TICK_MARKS = SLIDER_MARK_HOURS.map((value) => ({ value }));

function getSliderState(minHour: string, maxHour: string): {
  value: [number, number];
  hasCommittedRange: boolean;
} {
  const minH = parseInt(minHour, 10);
  const maxH = parseInt(maxHour, 10);
  const hasCommittedRange =
    minHour !== "" &&
    maxHour !== "" &&
    !Number.isNaN(minH) &&
    !Number.isNaN(maxH) &&
    minH >= 0 &&
    minH <= 23 &&
    maxH >= 0 &&
    maxH <= 23 &&
    minH < maxH;
  if (hasCommittedRange) {
    return { value: [minH, maxH], hasCommittedRange: true };
  }
  return { value: DEFAULT_BUSINESS_HOURS_RANGE, hasCommittedRange: false };
}

export interface BusinessHoursRangeSliderProps {
  minHour: string;
  maxHour: string;
  onChange: (minHour: string, maxHour: string) => void;
  disabled?: boolean;
  error?: string;
}

function BusinessHoursRangeSlider({
  minHour,
  maxHour,
  onChange,
  disabled = false,
  error,
}: BusinessHoursRangeSliderProps) {
  const { value: sliderValue, hasCommittedRange } = useMemo(
    () => getSliderState(minHour, maxHour),
    [minHour, maxHour]
  );
  const [rangeLo, rangeHi] = sliderValue;

  const handleSliderChange = useCallback(
    (_e: Event, value: number | number[]) => {
      if (!Array.isArray(value)) return;
      let [lo, hi] = value;
      lo = Math.max(0, Math.min(23, lo));
      hi = Math.max(0, Math.min(23, hi));
      if (hi - lo < 1) {
        if (hi < 23) hi = lo + 1;
        else lo = hi - 1;
      }
      onChange(String(lo), String(hi));
    },
    [onChange]
  );

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        borderRadius: 2,
        bgcolor: "#FAFBFC",
        p: 2.5,
        mt: 1,
      }}
    >
      <Typography variant="subtitle2" component="label" id="business-hours-slider-label" sx={{ color: "#070639" }}>
        Business hours window
      </Typography>
      <Typography variant="caption" display="block" sx={{ mt: 0.75, color: "#6B7280", fontSize: 11, lineHeight: 1.5 }}>
        Choose the first and last hour of the day when slots may be offered (full 24-hour day). Labels use AM/PM;
        stored values are still hour numbers 0–23.
      </Typography>

      {hasCommittedRange ? (
        <Box sx={{ mt: 2 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, color: "#070639", letterSpacing: "-0.01em" }}>
            {formatHourClock(rangeLo)} – {formatHourClock(rangeHi)}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.5 }}>
            {rangeHi - rangeLo}-hour span · hours {rangeLo}–{rangeHi}
          </Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 2 }}>
          Adjust the slider to set your window (handles show a typical default until values are saved).
        </Typography>
      )}

      <Box sx={{ mt: 2.5, px: 1.5, pb: 1, pt: 0.5 }}>
        <Slider
          aria-labelledby="business-hours-slider-label"
          value={sliderValue as number[]}
          onChange={handleSliderChange}
          min={0}
          max={23}
          step={1}
          disabled={disabled}
          valueLabelDisplay="auto"
          valueLabelFormat={(v: number) => formatHourClock(v)}
          marks={TICK_MARKS}
          disableSwap
          sx={{
            height: 36,
            color: "#2563EB",
            // MUI adds marginBottom for built-in mark labels; we use a custom label row instead.
            "&.MuiSlider-marked": {
              marginBottom: 0,
            },
            "& .MuiSlider-rail": {
              opacity: 1,
              height: 10,
              borderRadius: 5,
              backgroundColor: "#E5E7EB",
            },
            "& .MuiSlider-track": {
              height: 10,
              borderRadius: 5,
              border: "none",
              background: "linear-gradient(90deg, #3B82F6 0%, #1D4ED8 100%)",
            },
            "& .MuiSlider-thumb": {
              width: 22,
              height: 22,
              backgroundColor: "#fff",
              border: "2px solid #2563EB",
              boxShadow: "0 2px 6px rgba(7, 6, 57, 0.18)",
              "&:hover, &.Mui-focusVisible, &.Mui-active": {
                boxShadow: "0 2px 10px rgba(37, 99, 235, 0.35)",
              },
            },
            "& .MuiSlider-mark": {
              width: 2,
              height: 10,
              borderRadius: 1,
              backgroundColor: "#9CA3AF",
              top: "50%",
              transform: "translate(-1px, -50%)",
            },
            "& .MuiSlider-markActive": {
              backgroundColor: "#fff",
              opacity: 0.9,
            },
            "& .MuiSlider-valueLabel": {
              backgroundColor: "#070639",
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 600,
              "&:before": {
                display: "none",
              },
            },
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: "relative",
            width: "100%",
            minHeight: 24,
            mt: 2.25,
          }}
        >
          {SLIDER_MARK_HOURS.map((h) => (
            <Typography
              key={h}
              component="span"
              variant="caption"
              sx={{
                position: "absolute",
                left: `${(h / 23) * 100}%`,
                transform: "translateX(-50%)",
                fontSize: 12,
                fontWeight: 600,
                color: "#1F2937",
                lineHeight: 1.25,
                whiteSpace: "nowrap",
                top: 0,
              }}
            >
              {formatHourClock(h)}
            </Typography>
          ))}
        </Box>
      </Box>

      {error ? (
        <Typography variant="caption" color="error" display="block" sx={{ mt: 1.5, px: 0.5 }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
}

export { BusinessHoursRangeSlider };
